import { OnboardingChecklist, UserAction } from "@brookbush/types";

import { zodResolver } from "@hookform/resolvers/zod";
import posthog from "posthog-js";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { z } from "zod/v4";

import Button from "./Button";
import Dialog from "./Dialog";
import PhoneFields from "./OnboardingWizard/fields/PhoneFields";
import { useUserAction } from "~/hooks";
import useOnboarding from "~/hooks/useOnboarding";
import useOnboardingChecklist from "~/hooks/useOnboardingChecklist";

const schema = z.object({
	phoneNumber: z
		.string()
		.regex(/^\+[1-9]\d{1,14}$/, "Enter a valid phone number"),
});

type FormValues = z.infer<typeof schema>;

const PhoneNumberModal = () => {
	const { userAction, resetUserAction } = useUserAction();
	const { handleUpdate, onboarding } = useOnboarding();
	const { handleCompleteChecklistItem } = useOnboardingChecklist();

	const isOpen = userAction === UserAction.OPEN_PHONE_NUMBER;

	const methods = useForm<FormValues>({
		resolver: zodResolver(schema),
		values: { phoneNumber: onboarding?.smsPhoneNumber ?? "" },
	});

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = methods;

	const onSubmit = async ({ phoneNumber }: FormValues) => {
		posthog.setPersonProperties({ phone_number: phoneNumber });
		await handleUpdate({ phoneNumber });
		await handleCompleteChecklistItem(OnboardingChecklist.ADD_PHONE_NUMBER);
		toast.success("Phone number saved!");
		resetUserAction();
	};

	return (
		<Dialog
			isOpen={isOpen}
			onClose={resetUserAction}
			title="Get updates via text"
			className="max-w-md"
		>
			<p className="text-slate-600 text-sm dark:text-slate-400">
				Get important updates via SMS. Select your country and enter your
				number.
			</p>

			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
				<FormProvider {...methods}>
					<PhoneFields isOptional={false} />
				</FormProvider>

				<Button type="submit" disabled={isSubmitting}>
					Submit
				</Button>
			</form>
		</Dialog>
	);
};

export default PhoneNumberModal;
